import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { SalesInvoiceForm } from "@/features/sales-invoices";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditSalesInvoice = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchSalesInvoice = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`sales-invoices/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withPaymentTerm: true,
            withProductVariants: true,
            withSalesInvoiceExpenses: true,
            withSalesDownPaymentAllocations: true,
            withCreator: true,
          },
        });

        setDefaultValues({
          referenceNumber: data.referenceNumber,
          transactionDate: dayjs(data.transactionDate),
          seller: data.seller,
          buyer: data.buyer,
          buyerableType: data.buyerableType,
          buyerReferenceNumber: data.buyerReferenceNumber,
          paymentTerm: data.paymentTerm,
          note: data.note,
          taxable: data.taxable,
          totalIncludesTax: data.totalIncludesTax,
          taxAmount: data.taxAmount,
          taxPercentage: data.taxPercentage,
          discount: data.discount,
          discountInput: data.discountAmount,
          discountType: data.discountType,
          salesInvoiceProductVariants: data.salesInvoiceProductVariants.map(
            (variant) => ({
              id: variant.id,
              salesOrderProductVariantId: variant.salesOrderProductVariantId,
              name: variant.name,
              sku: variant.sku,
              unitOfMeasurement: variant.product.unitOfMeasurement.name,
              quantity: variant.quantity,
              discount: variant.discount,
              price: variant.price,
              note: variant.note,
              returnedQuantity: variant.returnedQuantity,
            })
          ),
          salesInvoiceExpenses:
            data.salesInvoiceExpenses?.map((expense) => ({
              id: expense.id,
              outletChartOfAccount: expense.outletChartOfAccount,
              amount: expense.amount,
              note: expense.note,
              returnedAmount: expense.returnedAmount,
            })) || [],
          salesDownPaymentAllocations:
            data.salesDownPaymentAllocations?.map((allocation) => {
              const sourceSdp = allocation.sourceSalesInvoice || {};
              const remaining = Number(sourceSdp.availableAmount ?? 0);
              const ownAmount = Number(allocation.amount ?? 0);
              return {
                id: allocation.id,
                isNewlyAdded: false,
                sourceSalesInvoiceId:
                  allocation.sourceSalesInvoiceId ?? sourceSdp.id,
                referenceNumber:
                  allocation.sourceReferenceNumber ?? sourceSdp.referenceNumber,
                // Cap the editable amount at what this allocation can grow to:
                // the SDP's remaining (excluding this SI) plus what this SI already holds.
                availableAmount: remaining + ownAmount,
                amount: ownAmount,
                note: sourceSdp.note ?? null,
              };
            }) || [],
        });
      } catch (error) {
        const errorMessage =
          getErrorMessage(error, "Gagal memperbarui faktur penjualan");
        showSnackbar(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSalesInvoice();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formData) => {
    try {
      const { sellerId, ...payload } = formData;

      await apiOutletClient.patch(
        `sales-invoices/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": sellerId,
          },
        }
      );
      showSnackbar("Faktur penjualan berhasil diperbarui", "success");
      navigate(-1);
    } catch (error) {
      const errorMessage =
        getErrorMessage(error, "Gagal memperbarui faktur penjualan");
      showSnackbar(errorMessage);
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Faktur Penjualan"
      breadcrumbs={[
        { title: "Faktur Penjualan", path: "/sales/sales-invoices" },
        {
          title: referenceNumber,
          path: `/sales/sales-invoices/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <SalesInvoiceForm
        editMode={true}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
